import { Component, Inject, OnDestroy, OnInit, viewChild } from '@angular/core';
import {
  MSAL_GUARD_CONFIG,
  MsalBroadcastService,
  MsalGuardConfiguration,
  MsalService,
} from '@azure/msal-angular';
import {
  AccountInfo,
  AuthenticationResult,
  InteractionStatus,
  RedirectRequest,
} from '@azure/msal-browser';
import { forkJoin, of, Subject } from 'rxjs';

import { AuthService } from '../../core/services/auth.service';
import { CurrentUserModel } from '../../core/models/currentuser';
import { catchError, filter, takeUntil } from 'rxjs/operators';
import { AuthUserService } from '../../api/elite/routes/auth.service';
import { CommonModule } from '@angular/common';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { NgProgressbar } from 'ngx-progressbar';

@Component({
  standalone: true,
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.sass'],
  imports: [
    CommonModule,
    MatSidenavModule,
    MatCardModule,
    MatButtonModule,
    NgProgressbar,
  ],
})
export class LoginComponent implements OnInit, OnDestroy {
  progressBar = viewChild(NgProgressbar);
  isIframe = false;
  isAuthenticated = false;

  // tslint:disable-next-line:variable-name
  private readonly _destroying$ = new Subject<void>();

  constructor(
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private authService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private legacyAuthService: AuthService,
    private authUserService: AuthUserService
  ) {}

  ngOnInit(): void {
    this.progressBar();
    this.isIframe = window !== window.parent && !window.opener;
    this.setLoginDisplay();
    this.authService.handleRedirectObservable().subscribe({
      next: (result: AuthenticationResult) => {
        if (result) {
          this.setLoginDisplay();
          this.checkAndSetActiveAccount();
        }
      },
      error: (error) => console.log(error),
    });
    this.msalBroadcastService.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) => status === InteractionStatus.None
        ),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.setLoginDisplay();
        this.checkAndSetActiveAccount();
      });
  }

  setLoginDisplay(): void {
    this.isAuthenticated =
      this.authService.instance.getAllAccounts().length > 0;
  }

  checkAndSetActiveAccount(): void {
    /**
     * If no active account set but there are accounts signed in, sets first account to active account
     * To use active account set here, subscribe to inProgress$ first in your component
     * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
     */
    const activeAccount = this.authService.instance.getActiveAccount();
    if (
      !activeAccount &&
      this.authService.instance.getAllAccounts().length > 0
    ) {
      const [accounts] = this.authService.instance.getAllAccounts();
      this.authService.instance.setActiveAccount(accounts);
    }
    if (activeAccount) {
      this.signOn(activeAccount);
    }
  }

  signOn(activeAccount: AccountInfo): void {
    const userDto = {
      uid: activeAccount.localAccountId,
      firstName: activeAccount.name,
      lastName: '',
      email:
        activeAccount.username ||
        activeAccount.idTokenClaims.preferred_username,
    };
    forkJoin({
      user: this.authUserService.signOnUser(userDto),
      legacy: this.legacyAuthService
        .signOnUser(userDto)
        .pipe(catchError(() => of(null))),
    }).subscribe(({ user }) => {
      const currentUser: CurrentUserModel = {
        id: user.id,
        uid: user.uid,
        number: user.number,
        role: activeAccount.idTokenClaims.roles,
        givenName: user.name,
        surname: user.lastName,
        userName: userDto.email,
      };
      this.legacyAuthService.setActiveUser(currentUser);
    });
  }

  loginRedirect(): void {
    if (this.msalGuardConfig.authRequest) {
      this.authService.loginRedirect({
        ...this.msalGuardConfig.authRequest,
      } as RedirectRequest);
    } else {
      this.authService.loginRedirect();
    }
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }
}
